import { DataGrid } from '@material-ui/data-grid';
import { find, forEach, sortBy } from 'lodash';
import React, { useEffect, useState, useContext, useMemo } from 'react';
import AppContext from '../../../App/AppContext';
import { PrimaryButton, SelectBox } from '../../../Core/FormInput';
import { assignedTechAndList, assignedTechnicianToJob, getAssignJobList } from '../../../Core/Service/workflowData-service';
import { Checkbox, ListItemText, MenuItem, Select } from '@material-ui/core';
import './assign-technician.scss';
import { AppStorage } from '../../../Core/Service/storage-service';
import { createLogger } from '@microsoft/signalr/dist/esm/Utils';

const parseAssignedEvents = (t) => {
    let raw = t.ScheduleEventIDs || t.scheduleEventIDs || t.scheduleEvents || t.ScheduleEvents || t.events || t.technicianScheduleEvents || t.assignedEvents;

    if (typeof raw === 'string' && (raw.trim().startsWith('[') || raw.trim().startsWith('{'))) {
        try {
            raw = JSON.parse(raw);
        } catch (e) {
            console.warn('Failed to parse schedule events JSON', e);
        }
    }

    if (Array.isArray(raw)) {
        return raw.map((item) => {
            if (typeof item === 'object' && item !== null) {
                return {
                    scheduleEventID: item.scheduleEventID !== undefined ? item.scheduleEventID : item.id,
                    statusID: item.statusID !== undefined ? item.statusID : item.statusId
                };
            }
            return {
                scheduleEventID: Number(item) || item,
                statusID: t.statusID || t.vehicleInspectionStatusID || null
            };
        });
    }

    if (typeof raw === 'string') {
        return raw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .map((id) => ({
                scheduleEventID: Number(id) || id,
                statusID: t.statusID || t.vehicleInspectionStatusID || null
            }));
    }

    if (t.scheduleEventID !== null && t.scheduleEventID !== undefined && t.scheduleEventID !== '') {
        return [
            {
                scheduleEventID: Number(t.scheduleEventID) || t.scheduleEventID,
                statusID: t.statusID || t.vehicleInspectionStatusID || null
            }
        ];
    }

    if (t.inspectionSheetTypeID !== null && t.inspectionSheetTypeID !== undefined && t.inspectionSheetTypeID !== '') {
        return [
            {
                scheduleEventID: Number(t.inspectionSheetTypeID) || t.inspectionSheetTypeID,
                statusID: t.statusID || t.vehicleInspectionStatusID || null
            }
        ];
    }

    return [];
};

const isEventLocked = (statusID) => {
    return statusID !== null && statusID !== undefined && statusID !== '' && Number(statusID) !== 1;
};

const AssignTech = (props) => {
    const urlParams = new URLSearchParams(props.location?.search ? props.location.search.toLowerCase() : '');
    const navigateToDashboard = urlParams.get('navigate') === 'true';
    const fromStep = urlParams.get('fromstep') === 'true';
    const { hideTopNavAndMenu, showToast } = useContext(AppContext);
    const [dropDown, setDropDown] = useState([]);
    const getClass = (params) => {
        if (params.row.isAssigned) {
            return 'is-assigned';
        } else if (params.row.selectedNext) {
            return 'will-be-assigned';
        }
    };
    const defaultColumns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 0.7,
            minWidth: 90
        },
        {
            field: 'jobAssigned',
            headerName: 'Jobs',
            description: 'Jobs Assigned',
            width: 100,
            minWidth: 50,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'isAssigned',
            headerName: 'Assign',
            width: 150,
            minWidth: 50,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'insp',
            headerName: 'Events',
            flex: 1,
            minWidth: 110,
            sortable: false
        }
    ].map((c) => ({ ...c, cellClassName: getClass }));

    let isAssignTechnicianAllowed = AppStorage.getCanAssignTechnicianAllowed();

    const [state, setState] = useState({
        jobId: props.jobId || props.match?.params?.jobId,
        technicians: [],
        isEnable: false
    });

    const isStatusDisabled = (row) => {
        return row?.vehicleInspectionStatusID !== null && row?.vehicleInspectionStatusID !== undefined && Number(row?.vehicleInspectionStatusID) !== 1;
    };

    const onCheckboxClick = (id) => (e) => {
        const { checked } = e.target;
        if (isAssignTechnicianAllowed) {
            setState((st) => {
                let newSt = { ...st };
                const rw = find(newSt.technicians, (t) => t.id === id);
                if (rw && !isStatusDisabled(rw)) {
                    const hasLocked = (rw.assignedEvents || []).some((ev) => isEventLocked(ev.statusID));
                    if (!checked && hasLocked) {
                        showToast && showToast('Cannot unassign technician with in-progress events');
                        return newSt;
                    }
                    rw.selectedNext = checked;
                    rw.isAssigned = checked;
                    rw.mode = checked ? 'A' : 'D';
                    if (!checked) {
                        rw.assignedEvents = [];
                        rw.ScheduleEventIDs = [];
                        rw.scheduleEventIDs = [];
                        rw.scheduleEventID = null;
                        rw.inspectionSheetTypeID = null;
                    }
                    newSt.technicians = [...newSt.technicians];
                    newSt.isEnable = true;
                }
                return newSt;
            });
        }
    };

    const onSelect = (id, selectedValues) => {
        if (isAssignTechnicianAllowed) {
            setState((st) => {
                let newSt = { ...st };
                const rw = find(newSt.technicians, (t) => t.id === id);
                if (rw && !isStatusDisabled(rw)) {
                    const currentAssigned = rw.assignedEvents || [];
                    const lockedIds = currentAssigned.filter((e) => isEventLocked(e.statusID)).map((e) => e.scheduleEventID);

                    const rawValues = Array.isArray(selectedValues) ? selectedValues : selectedValues ? [selectedValues] : [];
                    const values = Array.from(new Set([...rawValues.map((v) => Number(v) || v), ...lockedIds.map((v) => Number(v) || v)]));

                    const updatedAssigned = values.map((eventId) => {
                        const existing = currentAssigned.find((e) => String(e.scheduleEventID) === String(eventId));
                        return existing || { scheduleEventID: eventId, statusID: null };
                    });

                    rw.assignedEvents = updatedAssigned;
                    rw.ScheduleEventIDs = values;
                    rw.scheduleEventIDs = values;
                    rw.scheduleEventID = values.length > 0 ? values[0] : null;
                    rw.inspectionSheetTypeID = rw.scheduleEventID;
                    if (values.length > 0) {
                        rw.isAssigned = true;
                        rw.selectedNext = true;
                        rw.mode = 'A';
                    } else {
                        rw.mode = rw.isAssigned ? 'A' : 'D';
                    }
                    newSt.technicians = [...newSt.technicians];
                    newSt.isEnable = true;
                }
                return newSt;
            });
        }
    };

    const columns = () => {
        let allCols = [...defaultColumns];
        let col = allCols.find((c) => c.field === 'isAssigned');
        if (col) {
            col.renderCell = (params) => {
                const hasLocked = (params.row.assignedEvents || []).some((ev) => isEventLocked(ev.statusID));
                const disabled = !isAssignTechnicianAllowed || isStatusDisabled(params.row) || hasLocked;
                return (
                    <Checkbox
                        size="small"
                        style={{ padding: 2 }}
                        checked={Boolean(params.row.isAssigned)}
                        onChange={onCheckboxClick(params.row.id)}
                        disabled={disabled}
                    />
                );
            };
        }

        let inspCol = allCols.find((c) => c.field === 'insp');
        if (inspCol) {
            inspCol.renderCell = (params) => {
                const isSelectDisabled = !isAssignTechnicianAllowed || isStatusDisabled(params.row);
                const assignedEvents = params.row.assignedEvents || [];
                const rawEventIds =
                    params.row.ScheduleEventIDs ||
                    params.row.scheduleEventIDs ||
                    assignedEvents.map((e) => e.scheduleEventID) ||
                    (params.row.scheduleEventID ? [params.row.scheduleEventID] : []);
                const currentRowEventIds = rawEventIds.map((id) => {
                    const match = dropDown.find((d) => String(d.id) === String(id));
                    return match ? match.id : id;
                });

                const isOptionLocked = (eventId) => {
                    const found = assignedEvents.find((e) => String(e.scheduleEventID) === String(eventId));
                    return Boolean(found && isEventLocked(found.statusID));
                };

                const selectedByOthers = [];
                state.technicians
                    .filter((t) => t.id !== params.row.id)
                    .forEach((t) => {
                        const ids =
                            t.ScheduleEventIDs ||
                            t.scheduleEventIDs ||
                            (t.assignedEvents ? t.assignedEvents.map((e) => e.scheduleEventID) : []) ||
                            (t.scheduleEventID ? [t.scheduleEventID] : []);
                        ids.forEach((id) => {
                            if (id !== null && id !== undefined && id !== '') {
                                selectedByOthers.push(String(id));
                            }
                        });
                    });

                const availableOptions = dropDown.filter(
                    (d) => !selectedByOthers.includes(String(d.id)) || currentRowEventIds.some((s) => String(s) === String(d.id))
                );

                const hasUnlockableEvents = currentRowEventIds.some((id) => !isOptionLocked(id));

                return (
                    <div style={{ width: '100%', padding: '3px 0px' }}>
                        <Select
                            multiple
                            displayEmpty
                            value={currentRowEventIds}
                            onChange={(e) => {
                                const val = e.target.value;
                                const lockedIds = assignedEvents.filter((ev) => isEventLocked(ev.statusID)).map((ev) => ev.scheduleEventID);

                                if (val.includes('__clear__')) {
                                    onSelect(params.row.id, lockedIds);
                                } else {
                                    const finalVal = Array.from(new Set([...val, ...lockedIds]));
                                    onSelect(params.row.id, finalVal);
                                }
                            }}
                            disabled={isSelectDisabled}
                            disableUnderline
                            renderValue={(selected) => {
                                if (!selected || selected.length === 0) {
                                    return <span style={{ color: '#999', fontSize: 12 }}>None</span>;
                                }
                                const selectedNames = dropDown.filter((d) => selected.some((s) => String(s) === String(d.id))).map((d) => d.name);
                                return (
                                    <span
                                        style={{
                                            fontSize: 12,
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                        title={selectedNames.join(', ')}
                                    >
                                        {selectedNames.join(', ')}
                                    </span>
                                );
                            }}
                            style={{
                                width: '100%',
                                height: 30,
                                border: '1px solid #ccc',
                                borderRadius: 4,
                                backgroundColor: isSelectDisabled ? '#f1f5f9' : '#fff',
                                fontSize: 13,
                                cursor: isSelectDisabled ? 'not-allowed' : 'pointer'
                            }}
                            SelectDisplayProps={{
                                style: {
                                    padding: '3px 8px',
                                    fontSize: 13,
                                    lineHeight: '22px'
                                }
                            }}
                            MenuProps={{
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left'
                                },
                                getContentAnchorEl: null,
                                PaperProps: {
                                    style: {
                                        maxHeight: 250,
                                        minWidth: 200
                                    }
                                }
                            }}
                        >
                            {hasUnlockableEvents && (
                                <MenuItem value="__clear__" dense style={{ padding: '2px 8px', borderBottom: '1px solid #eee' }}>
                                    <ListItemText
                                        primary="None (Clear All)"
                                        primaryTypographyProps={{ style: { fontSize: 12, fontStyle: 'italic', color: '#f50057' } }}
                                    />
                                </MenuItem>
                            )}
                            {availableOptions.map((d) => {
                                const isChecked = currentRowEventIds.some((s) => String(s) === String(d.id));
                                const isLocked = isOptionLocked(d.id);
                                return (
                                    <MenuItem
                                        key={d.id}
                                        value={d.id}
                                        dense
                                        disabled={isLocked}
                                        style={{
                                            padding: '2px 8px',
                                            opacity: isLocked ? 0.7 : 1,
                                            cursor: isLocked ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        <Checkbox size="small" checked={isChecked} disabled={isLocked} style={{ padding: '2px 6px' }} />
                                        <ListItemText primary={d.name} primaryTypographyProps={{ style: { fontSize: 12 } }} />
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </div>
                );
            };
        }
        return allCols;
    };

    const getAssignedTechAndOthers = async () => {
        const res = await assignedTechAndList(state.jobId);
        if (res.success) {
            const mappedData = (res.data || []).map((t) => {
                const assignedEvents = parseAssignedEvents(t);
                const eventIDs = assignedEvents.map((e) => e.scheduleEventID);
                return {
                    ...t,
                    assignedEvents: assignedEvents,
                    ScheduleEventIDs: eventIDs,
                    scheduleEventIDs: eventIDs,
                    scheduleEventID: eventIDs.length > 0 ? eventIDs[0] : null,
                    isEventFromApi: isStatusDisabled(t)
                };
            });
            setState((st) => ({ ...st, technicians: mappedData }));
        } else {
            showToast(res.message);
        }
    };

    useEffect(() => {
        let fetchList = async () => {
            let JobDropdownList = await getAssignJobList();
            if (JobDropdownList.success) {
                setDropDown(JobDropdownList.data.list);
            }
        };
        fetchList();
    }, []);

    useEffect(() => {
        if (props.jobId && props.jobId !== state.jobId) {
            setState((st) => ({ ...st, jobId: props.jobId }));
        }
    }, [props.jobId]);

    useEffect(() => {
        fromStep && hideTopNavAndMenu();
        if (state.jobId) {
            getAssignedTechAndOthers();
        }
    }, [state.jobId]);

    console.log('state', state.technicians);

    const saveTechnicianAndMove = async () => {
        const techToSave = state.technicians.filter((c) => c.mode);
        const res = await assignedTechnicianToJob(state.jobId, techToSave);
        if (res.success) {
            showToast(res.message);
            if (res.success && fromStep) {
                setTimeout(() => {
                    window.top.postMessage({ type: navigateToDashboard ? 'navigate-dashboard' : 'close-modal' }, '*');
                }, 1000);
            }
            if (props.handleCancel) {
                props.handleCancel(true);
            }
        }
        if (!res.success) {
            console.error(res);
        }
    };

    const isModal = Boolean(props.handleCancel);

    return (
        <>
            <div
                className={`assign-tech-grid-container ${isModal ? 'is-modal' : ''}`}
                style={isModal ? { height: 'calc(100vh - 220px)', minHeight: 400, margin: '0 auto', width: '100%', maxWidth: '100%' } : {}}
            >
                <div className="grid-wrapper" style={isModal ? { height: 'calc(100vh - 280px)', minHeight: 340 } : {}}>
                    <DataGrid
                        rows={state.technicians}
                        headerHeight={40}
                        rowHeight={40}
                        hideFooter={true}
                        columns={columns()}
                        pageSize={100}
                        disableColumnMenu={true}
                        showColumnRightBorder={true}
                    />
                </div>
                <PrimaryButton fullWidth className="assign-btn mt-20" onClick={saveTechnicianAndMove} disabled={!state.isEnable}>
                    Assign/Unassign Technician
                </PrimaryButton>
            </div>
        </>
    );
};

export default AssignTech;
